import { useCallback, useEffect, useState } from "react";

const optionsCacheByQuery = new WeakMap();

const clearOptionsCache = (queryApi) => {
  optionsCacheByQuery.delete(queryApi);
};

const getOptionsCache = (queryOptions, mapOption) => {
  let cacheByMapper = optionsCacheByQuery.get(queryOptions);
  if (!cacheByMapper) {
    cacheByMapper = new WeakMap();
    optionsCacheByQuery.set(queryOptions, cacheByMapper);
  }

  let cache = cacheByMapper.get(mapOption);
  if (!cache) {
    cache = new Map();
    cacheByMapper.set(mapOption, cache);
  }

  return cache;
};

const hasNoResultPrefix = (cache, keyword) => {
  if (!keyword) return false;

  for (let index = keyword.length - 1; index > 0; index -= 1) {
    const cachedOptions = cache.get(keyword.slice(0, index));
    if (Array.isArray(cachedOptions) && cachedOptions.length === 0) {
      return true;
    }
  }

  return false;
};

const mergeOptions = (currentOptions, nextOptions) => [
  ...currentOptions.filter(
    (currentOption) =>
      !nextOptions.some((option) => option.value === currentOption.value),
  ),
  ...nextOptions,
];

const useAsyncOptionField = ({
  queryOptions,
  mapOption,
  getDefaultKeyword,
  isDefaultOption,
  onSelect,
}) => {
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [cacheKey, setCacheKey] = useState(1); // stateful key to refresh react-select cache

  const fetchOptions = useCallback(
    async (keyword = "") => {
      if (!queryOptions) return [];

      const searchKeyword = `${keyword || ""}`.trim();
      const cache = getOptionsCache(queryOptions, mapOption);
      if (cache.has(searchKeyword)) {
        return cache.get(searchKeyword);
      }

      if (hasNoResultPrefix(cache, searchKeyword)) {
        cache.set(searchKeyword, []);
        return [];
      }

      const request = queryOptions(
        searchKeyword ? { keyword: searchKeyword } : {},
      ).then((response) => {
        const responseBody = response?.data;
        if (responseBody?.responseCode !== "0000") return [];

        return (responseBody.data || []).map(mapOption).filter(Boolean);
      });
      cache.set(searchKeyword, request);

      const options = await request;
      cache.set(searchKeyword, options);
      return options;
    },
    [mapOption, queryOptions],
  );

  const invalidateCache = useCallback(() => {
    clearOptionsCache(queryOptions);
    setOptions([]);
    setSelectedOption(null);
    setCacheKey((prevKey) => prevKey + 1); // increment the key to refresh react-select cache
  }, [queryOptions, setOptions, setSelectedOption]);

  useEffect(() => {
    if (!queryOptions) return undefined;

    let active = true;
    if (options.length === 0) {
      fetchOptions("").then((nextOptions) => {
        if (active) {
          setOptions((currentOptions) =>
            mergeOptions(currentOptions, nextOptions),
          );
        }
      });
    }
    return () => {
      active = false;
    };
  }, [fetchOptions, queryOptions, options]);

  useEffect(() => {
    const defaultKeyword = `${getDefaultKeyword?.() || ""}`.trim();
    if (!defaultKeyword) return undefined;

    let active = true;
    fetchOptions(defaultKeyword).then((nextOptions) => {
      if (!active) return;

      const defaultOption = nextOptions.find(isDefaultOption);
      if (!defaultOption) return;

      setSelectedOption(defaultOption);
      setOptions((currentOptions) => {
        const hasDefaultOption = currentOptions.some(
          (option) => option.value === defaultOption.value,
        );
        return hasDefaultOption
          ? currentOptions
          : [defaultOption, ...currentOptions];
      });
      onSelect(defaultOption, { isUserChange: false });
    });

    return () => {
      active = false;
    };
  }, [fetchOptions, getDefaultKeyword, isDefaultOption, onSelect]);

  const handleChange = useCallback(
    (event, nextOption) => {
      setSelectedOption(nextOption);
      if (nextOption) {
        setOptions((currentOptions) => {
          const hasSelectedOption = currentOptions.some(
            (option) => option.value === nextOption.value,
          );
          return hasSelectedOption
            ? currentOptions
            : [nextOption, ...currentOptions];
        });
      }
      onSelect(nextOption, { isUserChange: true });
    },
    [onSelect],
  );

  return {
    fetchOptions,
    handleChange,
    options,
    selectedOption,
    cacheKey,
    invalidateCache,
  };
};

export default useAsyncOptionField;
